/** Composes a term-theme garden scene + progress stats into a shareable PNG,
 *  then hands it to the native share sheet (mobile) or downloads it (desktop). */
export async function shareScene({ assetPrefix, sceneIndex, themeName, teReo, childName,
                             activeDays, latestLabel, shareSubtitle, year }) {
  const W = 1200;
  const H = 1050;
  const IMG_H = 675;
  const canvas = document.createElement('canvas');
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext('2d');

  ctx.fillStyle = '#FAF9F6';
  ctx.fillRect(0, 0, W, H);

  const img = new Image();
  img.crossOrigin = 'anonymous';
  img.src = `${assetPrefix}_${sceneIndex}.png`;
  await new Promise((resolve, reject) => {
    img.onload = resolve;
    img.onerror = reject;
  });
  const ratio = img.width / img.height;
  const targetRatio = W / IMG_H;
  let sx = 0, sy = 0, sw = img.width, sh = img.height;
  if (ratio > targetRatio) {
    sw = img.height * targetRatio;
    sx = (img.width - sw) / 2;
  } else {
    sh = img.width / targetRatio;
    sy = (img.height - sh) / 2;
  }
  ctx.drawImage(img, sx, sy, sw, sh, 0, 0, W, IMG_H);

  const stripY = IMG_H;
  ctx.fillStyle = '#FAF9F6';
  ctx.fillRect(0, stripY, W, H - stripY);
  ctx.fillStyle = '#3D7A5E';
  ctx.fillRect(0, stripY, W, 4);

  ctx.fillStyle = '#1D3A2E';
  ctx.font = 'bold 26px "Outfit", system-ui, sans-serif';
  ctx.textAlign = 'right';
  ctx.fillText('NestWise', W - 60, stripY + 60);
  ctx.font = '18px "Lexend", system-ui, sans-serif';
  ctx.fillStyle = '#3D7A5E';
  ctx.fillText(`Year ${year || 5} · Aotearoa`, W - 60, stripY + 90);

  ctx.textAlign = 'left';
  ctx.fillStyle = '#1D3A2E';
  ctx.font = 'bold 56px "Outfit", system-ui, sans-serif';
  const title = childName ? `${childName}'s ${themeName}` : `Our ${themeName}`;
  ctx.fillText(title, 60, stripY + 90);

  ctx.font = 'italic 22px "Lexend", system-ui, sans-serif';
  ctx.fillStyle = '#3D7A5E';
  ctx.fillText(shareSubtitle || teReo, 60, stripY + 130);

  ctx.font = '500 28px "Lexend", system-ui, sans-serif';
  ctx.fillStyle = '#475569';
  const daysText = `${activeDays} ${activeDays === 1 ? 'active day' : 'active days'}`;
  const unlockText = latestLabel ? ` · Latest: ${latestLabel}` : ' · Just getting started';
  ctx.fillText(daysText + unlockText, 60, stripY + 200);

  ctx.font = '20px "Patrick Hand", "Lexend", system-ui, sans-serif';
  ctx.fillStyle = '#94A3B8';
  ctx.fillText('Beautiful, curriculum-aligned learning for NZ homeschoolers', 60, stripY + 260);

  const blob = await new Promise(res => canvas.toBlob(res, 'image/png', 0.95));
  if (!blob) throw new Error('Could not create image');

  const filename = `${(childName || 'whanau').toLowerCase().replace(/\s+/g, '-')}-${themeName.toLowerCase().replace(/\s+/g, '-')}.png`;
  const file = new File([blob], filename, { type: 'image/png' });

  if (navigator.canShare && navigator.canShare({ files: [file] })) {
    try {
      await navigator.share({
        files: [file],
        title,
        text: `${title} — ${daysText} on NestWise 🌿`,
      });
      return 'shared';
    } catch (e) {
      if (e.name === 'AbortError') return 'cancelled';
    }
  }

  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
  return 'downloaded';
}
